import { useCallback, useEffect, useRef, useState } from "react";
import { nearestRow } from "@/lib/nearestRow";

/**
 * Touch-friendly long-press-and-drag reordering.
 *
 * HTML5 drag-and-drop doesn't fire on mobile browsers, so this hook
 * reimplements the gesture using touch events:
 *   • finger down → start a 400 ms timer
 *   • if the finger moves more than `moveThreshold` px before the timer
 *     fires, cancel it (user is trying to scroll)
 *   • when the timer fires, enter "drag mode": vibrate + lock scrolling
 *   • finger moves → hit-test the element under the finger via
 *     `document.elementFromPoint`, surface the current drop target + the
 *     "above/below" half of the row
 *   • finger up → run `onReorder(newList)`
 *
 * Row elements must carry `data-reorder-id="<id>"` so the hit-test can find
 * them. The hook returns an `onTouchStart` factory you attach to each row
 * and shared drag state (`dragId`, `overId`, `overPos`) you render into
 * your drop indicators.
 */
export function useLongPressReorder({
  items,
  idOf,
  onReorder,
  longPressMs = 400,
  moveThreshold = 10,
}) {
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [overPos, setOverPos] = useState(null);

  // Mirrors in refs so the global listeners always read fresh values.
  const timerRef = useRef(null);
  const startPosRef = useRef(null);
  const activeRef = useRef(false);
  const dragIdRef = useRef(null);
  const overIdRef = useRef(null);
  const overPosRef = useRef(null);
  const itemsRef = useRef(items);
  const idOfRef = useRef(idOf);
  const onReorderRef = useRef(onReorder);
  itemsRef.current = items;
  idOfRef.current = idOf;
  onReorderRef.current = onReorder;

  // Edge-auto-scroll state. While the user drags near the top/bottom of the
  // scrollable ancestor, we scroll that container for them (otherwise the
  // viewport is frozen by `touch-action: none` and they can't reach rows
  // off-screen).
  const scrollParentRef = useRef(null);
  const listRootRef = useRef(null);
  const lastYRef = useRef(0);
  const rafRef = useRef(null);
  const SCROLL_EDGE = 70;      // px from edge where auto-scroll kicks in
  const SCROLL_MAX_SPEED = 14; // px per frame at the very edge

  const findScrollParent = (el) => {
    let node = el && el.parentElement;
    while (node) {
      const s = window.getComputedStyle(node);
      if (/(auto|scroll|overlay)/.test(s.overflowY) && node.scrollHeight > node.clientHeight + 1) {
        return node;
      }
      node = node.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  };

  const stopAutoScroll = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const autoScrollLoop = () => {
    const el = scrollParentRef.current;
    if (!el || !activeRef.current) { rafRef.current = null; return; }
    const rect = el.getBoundingClientRect
      ? el.getBoundingClientRect()
      : { top: 0, bottom: window.innerHeight };
    const y = lastYRef.current;
    let delta = 0;
    if (y < rect.top + SCROLL_EDGE) {
      delta = -Math.min(SCROLL_MAX_SPEED, (rect.top + SCROLL_EDGE - y) / 3);
    } else if (y > rect.bottom - SCROLL_EDGE) {
      delta = Math.min(SCROLL_MAX_SPEED, (y - (rect.bottom - SCROLL_EDGE)) / 3);
    }
    if (delta !== 0) {
      el.scrollTop += delta;
      // After scrolling, re-hit-test under the finger so the drop indicator
      // follows new rows that just slid into view.
      const hit = nearestRow(listRootRef.current, y);
      if (hit) {
        const { id, pos } = hit;
        if (id !== dragIdRef.current) {
          if (overIdRef.current !== id || overPosRef.current !== pos) {
            overIdRef.current = id;
            overPosRef.current = pos;
            setOverId(id);
            setOverPos(pos);
          }
        }
      }
      rafRef.current = requestAnimationFrame(autoScrollLoop);
    } else {
      rafRef.current = null;
    }
  };

  const maybeStartAutoScroll = () => {
    if (!rafRef.current && activeRef.current && scrollParentRef.current) {
      rafRef.current = requestAnimationFrame(autoScrollLoop);
    }
  };

  const cancelTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const resetAll = useCallback(() => {
    cancelTimer();
    stopAutoScroll();
    scrollParentRef.current = null;
    activeRef.current = false;
    dragIdRef.current = null;
    overIdRef.current = null;
    overPosRef.current = null;
    setDragId(null);
    setOverId(null);
    setOverPos(null);
    document.documentElement.style.touchAction = "";
    document.documentElement.style.userSelect = "";
  }, []);

  const activateDrag = (id) => {
    activeRef.current = true;
    dragIdRef.current = id;
    setDragId(id);
    try { navigator.vibrate?.(30); } catch { /* ignore */ }
    // Resolve the scrollable list ancestor NOW so we can auto-scroll it
    // without unfreezing the viewport.
    const row = document.querySelector(`[data-reorder-id="${CSS.escape(id)}"]`);
    listRootRef.current = row && row.parentElement;
    scrollParentRef.current = findScrollParent(row);
    document.documentElement.style.touchAction = "none";
    document.documentElement.style.userSelect = "none";
  };

  const handleMove = useCallback((e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    if (!activeRef.current) {
      // Long-press pending: a real scroll gesture should cancel it.
      const sp = startPosRef.current;
      if (sp && Math.hypot(t.clientX - sp.x, t.clientY - sp.y) > moveThreshold) {
        cancelTimer();
      }
      return;
    }
    if (e.cancelable) e.preventDefault();
    lastYRef.current = t.clientY;
    maybeStartAutoScroll();
    const hit = nearestRow(listRootRef.current, t.clientY);
    if (!hit) return;
    const { id, pos } = hit;
    if (id === dragIdRef.current) {
      // Hovering our own row — hide the indicator.
      if (overIdRef.current !== null) {
        overIdRef.current = null; overPosRef.current = null;
        setOverId(null); setOverPos(null);
      }
      return;
    }
    if (overIdRef.current !== id || overPosRef.current !== pos) {
      overIdRef.current = id;
      overPosRef.current = pos;
      setOverId(id);
      setOverPos(pos);
    }
  }, [moveThreshold]);

  const handleEnd = useCallback(() => {
    if (!activeRef.current) { resetAll(); return; }
    const fromId = dragIdRef.current;
    const toId = overIdRef.current;
    const pos = overPosRef.current;
    resetAll();
    if (!fromId || !toId || fromId === toId) return;
    const list = itemsRef.current;
    const idOfFn = idOfRef.current;
    const from = list.findIndex((x) => idOfFn(x) === fromId);
    let to = list.findIndex((x) => idOfFn(x) === toId);
    if (from < 0 || to < 0) return;
    if (pos === "below") to += 1;
    if (from < to) to -= 1;
    const next = list.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorderRef.current?.(next);
  }, [resetAll]);

  useEffect(() => {
    const move = (e) => handleMove(e);
    const end = () => handleEnd();
    // `passive: false` is required to call preventDefault inside handleMove
    // when the user is dragging (otherwise the viewport scrolls).
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", end);
    window.addEventListener("touchcancel", end);
    return () => {
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", end);
      window.removeEventListener("touchcancel", end);
      resetAll();
    };
  }, [handleMove, handleEnd, resetAll]);

  const onTouchStart = (id) => (e) => {
    if (!e.touches || e.touches.length !== 1) return;
    const t = e.touches[0];
    startPosRef.current = { x: t.clientX, y: t.clientY };
    cancelTimer();
    timerRef.current = setTimeout(() => activateDrag(id), longPressMs);
  };

  return { dragId, overId, overPos, onTouchStart };
}
